/**
 * AnomalyFeed — cross-daemon list of audit findings sorted by severity then
 * recency. Spec 62 D13 Task 35.
 *
 * Default scope excludes `promotion_candidate` so the feed doesn't compete
 * with /admin/promotion-candidates. Severity filter narrows to the categories
 * that actually need attention (medium and up).
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ShieldAlert, Wrench } from 'lucide-react';
import { Badge, Card, EmptyState, FilterSelect, QueryBoundary, SkeletonList, sentenceCase } from '../ui';
import { listAdminDaemonFindingsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import type { DaemonFinding } from '../../generated/api/types.gen.js';

const SEVERITY_RANK: Record<string, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
  info: 0,
};

type MinSeverity = 'info' | 'low' | 'medium' | 'high' | 'critical';

const SEVERITY_OPTIONS: Array<{ value: MinSeverity; label: string }> = [
  { value: 'info', label: 'Info' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

interface AnomalyFeedProps {
  /** Hide findings of this category. Defaults to 'promotion_candidate' since
   * those have a dedicated tab. */
  excludeCategory?: string;
}

export function AnomalyFeed({ excludeCategory = 'promotion_candidate' }: AnomalyFeedProps) {
  const [minSeverity, setMinSeverity] = useState<MinSeverity>('info');

  // The severity filter is applied on the client, so one cached read serves every threshold.
  const query = useQuery(listAdminDaemonFindingsOptions({ query: { limit: 200 } }));

  const sorted = useMemo(() => {
    const list = (query.data ?? []).filter((f) => f.category !== excludeCategory);
    const cutoff = SEVERITY_RANK[minSeverity] ?? 0;
    return list
      .filter((f) => (SEVERITY_RANK[f.severity] ?? 0) >= cutoff)
      .sort((a, b) => {
        const ra = SEVERITY_RANK[a.severity] ?? 0;
        const rb = SEVERITY_RANK[b.severity] ?? 0;
        if (ra !== rb) return rb - ra;
        return b.createdAt.localeCompare(a.createdAt);
      });
  }, [query.data, excludeCategory, minSeverity]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted">Min severity</span>
        <FilterSelect
          label="Minimum severity"
          value={minSeverity}
          options={SEVERITY_OPTIONS}
          onChange={(value) => setMinSeverity(value as MinSeverity)}
        />
      </div>

      <QueryBoundary
        query={query}
        label="anomalies"
        compact
        loading={<SkeletonList count={3} label="Loading anomalies" />}
      >
        {() =>
          sorted.length === 0 ? (
            <EmptyState
              icon={ShieldAlert}
              title="No anomalies"
              description="Daemon findings at or above the selected severity will surface here as they're written."
            />
          ) : (
            <div className="space-y-2">
              {sorted.map((f) => (
                <Card key={f.id} className="p-3">
                  <div className="flex items-start gap-3">
                    <CategoryIcon category={f.category} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-medium text-heading">{f.title}</h4>
                        <SeverityBadge severity={f.severity} />
                        <Badge variant="neutral">{sentenceCase(f.category)}</Badge>
                        {f.daemonName && (
                          <span className="text-xs text-faint">
                            from <code className="bg-surface-alt px-1">{f.daemonName}</code>
                          </span>
                        )}
                      </div>
                      {f.description && <p className="text-sm text-muted mb-1">{f.description}</p>}
                      {f.recommendation && <p className="text-xs text-body italic">→ {f.recommendation}</p>}
                      <div className="text-xs text-faint mt-1">{new Date(f.createdAt).toLocaleString()}</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )
        }
      </QueryBoundary>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: DaemonFinding['severity'] }) {
  switch (severity) {
    case 'critical':
    case 'high':
      return (
        <Badge variant="danger">
          <AlertTriangle size={12} /> {sentenceCase(severity)}
        </Badge>
      );
    case 'medium':
      return <Badge variant="warning">{sentenceCase(severity)}</Badge>;
    case 'low':
      return <Badge variant="info">{sentenceCase(severity)}</Badge>;
    default:
      return <Badge variant="neutral">{sentenceCase(severity)}</Badge>;
  }
}

function CategoryIcon({ category }: { category: DaemonFinding['category'] }) {
  if (category === 'vulnerability') {
    return <ShieldAlert size={18} className="text-danger shrink-0 mt-0.5" />;
  }
  if (category === 'quality-issue' || category === 'optimization') {
    return <Wrench size={18} className="text-warning shrink-0 mt-0.5" />;
  }
  return <AlertTriangle size={18} className="text-muted shrink-0 mt-0.5" />;
}
